import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  DatePicker, 
  Space, 
  Button, 
  Statistic, 
  Table, 
  Select,
  Spin
} from 'antd';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  StockOutlined, 
  ShoppingCartOutlined, 
  UserOutlined, 
  DollarCircleOutlined,
  GiftOutlined,
  ShopOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import { format, startOfDay, endOfDay, subDays, subMonths } from 'date-fns';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/fr_FR';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [analytics, setAnalytics] = useState({
    overview: {
      totalRevenue: 0,
      totalOrders: 0,
      totalCustomers: 0,
      averageOrderValue: 0,
      totalProducts: 0,
      activeCustomers: 0
    },
    revenueEvolution: [],
    ordersEvolution: [],
    topProducts: [],
    topCustomers: [],
    salesByPaymentMethod: [],
    recentOrders: [],
    customerGrowth: [],
    pointsDistribution: []
  });

  // Fonction pour récupérer les analytics (avec useCallback pour éviter les re-renders)
  const fetchAnalytics = useCallback(async () => {
    console.log('fetchAnalytics');
    try {
      setLoading(true);
      
      // Conversion dayjs vers Date pour date-fns
      const startDate = startOfDay(dateRange[0].toDate());
      const endDate = endOfDay(dateRange[1].toDate());
      
      // Récupération de toutes les données en parallèle
      const [
        overviewRes,
        revenueRes,
        ordersRes,
        topProductsRes,
        topCustomersRes,
        salesMethodRes,
        recentOrdersRes,
        customerGrowthRes,
        pointsRes
      ] = await Promise.all([
        api.get('/analytics/overview', { 
          params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() }
        }),
        api.get('/analytics/revenue-evolution', { 
          params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() }
        }),
        api.get('/analytics/orders-evolution', { 
          params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() }
        }),
        api.get('/analytics/top-products', { 
          params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() }
        }),
        api.get('/analytics/top-customers', { 
          params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() }
        }),
        api.get('/analytics/sales-by-method', { 
          params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() }
        }),
        api.get('/analytics/recent-orders', { 
          params: { limit: 10 }
        }),
        api.get('/analytics/customer-growth', { 
          params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() }
        }),
        api.get('/analytics/points-distribution')
      ]);

      setAnalytics({
        overview: overviewRes.data,
        revenueEvolution: revenueRes.data,
        ordersEvolution: ordersRes.data,
        topProducts: topProductsRes.data,
        topCustomers: topCustomersRes.data,
        salesByPaymentMethod: salesMethodRes.data,
        recentOrders: recentOrdersRes.data,
        customerGrowth: customerGrowthRes.data,
        pointsDistribution: pointsRes.data
      });

    } catch (error) {
      console.error('Erreur lors du chargement des analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]); // dateRange comme dépendance de useCallback

  // Effet pour charger les analytics au montage et quand dateRange change
  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      fetchAnalytics();
    }
  }, [fetchAnalytics]); // fetchAnalytics comme dépendance (memoized par useCallback)

  // Gestion des périodes prédéfinies
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    const now = dayjs();
    let startDate;
    
    switch (period) {
      case '7d':
        startDate = now.subtract(7, 'day');
        break;
      case '30d':
        startDate = now.subtract(30, 'day');
        break;
      case '3m':
        startDate = now.subtract(3, 'month');
        break;
      case '6m':
        startDate = now.subtract(6, 'month');
        break;
      case '1y':
        startDate = now.subtract(1, 'year');
        break;
      default:
        startDate = now.subtract(30, 'day');
    }
    
    setDateRange([startDate, now]);
    // Plus besoin d'appeler fetchAnalytics ici, le useEffect se chargera du rechargement
  };

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange(dates);
      setSelectedPeriod('custom');
      // Plus besoin d'appeler fetchAnalytics ici, le useEffect se chargera du rechargement
    }
  };

  // Couleurs pour les graphiques (selon la charte Mercuri Shop)
  const chartColors = {
    primary: '#3b82f6',
    secondary: '#06b6d4',
    success: '#10b981',
    warning: '#f59e0b',
    gradient: ['#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444']
  };

  // Configuration pour les graphiques
  const formatCurrency = (value) => `${value?.toLocaleString()} FCFA`;
  const formatNumber = (value) => value?.toLocaleString();

  // Fonction helper pour formater les dates de manière sécurisée
  const formatDateSafe = (date, formatString = 'dd/MM/yyyy HH:mm') => {
    if (!date) return 'Date invalide';
    try {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) return 'Date invalide';
      return format(parsedDate, formatString);
    } catch (error) {
      return 'Date invalide';
    }
  };

  // Colonnes pour le tableau des commandes récentes
  const recentOrdersColumns = [
    {
      title: 'N° Commande',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (text) => `#${text}`,
    },
    {
      title: 'Client',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Montant',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span className={`px-2 py-1 rounded text-xs ${
          status === 'completed' ? 'bg-green-100 text-green-800' :
          status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {status === 'completed' ? 'Terminée' : 
           status === 'pending' ? 'En cours' : status}
        </span>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDateSafe(date, 'dd/MM/yyyy HH:mm'),
    },
  ];

  // Colonnes pour les top produits
  const topProductsColumns = [
    {
      title: 'Produit',
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: 'Quantité vendue',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      render: formatNumber,
    },
    {
      title: 'Chiffre d\'affaires',
      dataIndex: 'total_revenue',
      key: 'total_revenue',
      render: formatCurrency,
    },
  ];

  // Colonnes pour les top clients
  const topCustomersColumns = [
    {
      title: 'Client',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Commandes',
      dataIndex: 'total_orders',
      key: 'total_orders',
      render: formatNumber,
    },
    {
      title: 'Total dépensé',
      dataIndex: 'total_spent',
      key: 'total_spent',
      render: formatCurrency,
    },
  ];

  return (
    <div className="p-6 bg-gradient-to-br from-blue-50 via-white to-cyan-50 min-h-screen">
      {/* En-tête */}
      <div className="mb-8">
        <Title level={2} className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
          📊 Analytics Business
        </Title>
        <Text className="text-gray-600">
          Tableau de bord des performances de votre boutique
        </Text>
      </div>

      {/* Filtres */}
      <Card className="mb-6 shadow-sm border-0 bg-white/60 backdrop-blur-sm">
        <Space wrap align="center" className="w-full justify-between">
          <Space wrap>
            <Text strong>Période d'analyse :</Text>
            <Select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              className="w-32"
            >
              <Option value="7d">7 jours</Option>
              <Option value="30d">30 jours</Option>
              <Option value="3m">3 mois</Option>
              <Option value="6m">6 mois</Option>
              <Option value="1y">1 an</Option>
              <Option value="custom">Personnalisé</Option>
            </Select>
            <RangePicker
              locale={locale}
              value={dateRange}
              onChange={handleDateChange}
              format="DD/MM/YYYY"
              placeholder={['Date début', 'Date fin']}
              className="shadow-sm"
            />
          </Space>
          <Button 
            type="primary" 
            onClick={fetchAnalytics}
            loading={loading}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 border-0 shadow-sm"
          >
            Actualiser
          </Button>
        </Space>
      </Card>

      <Spin spinning={loading}>
        {/* Métriques principales */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} lg={6}>
            <Card className="shadow-sm border-0 bg-white/60 backdrop-blur-sm">
              <Statistic
                title="Chiffre d'affaires"
                value={analytics.overview.totalRevenue}
                formatter={formatCurrency}
                prefix={<DollarCircleOutlined className="text-green-500" />}
                valueStyle={{ color: '#059669' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="shadow-sm border-0 bg-white/60 backdrop-blur-sm">
              <Statistic
                title="Commandes"
                value={analytics.overview.totalOrders}
                formatter={formatNumber}
                prefix={<ShoppingCartOutlined className="text-blue-500" />}
                valueStyle={{ color: '#3b82f6' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="shadow-sm border-0 bg-white/60 backdrop-blur-sm">
              <Statistic
                title="Clients actifs"
                value={analytics.overview.activeCustomers}
                formatter={formatNumber}
                prefix={<UserOutlined className="text-cyan-500" />}
                valueStyle={{ color: '#06b6d4' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="shadow-sm border-0 bg-white/60 backdrop-blur-sm">
              <Statistic
                title="Panier moyen"
                value={analytics.overview.averageOrderValue}
                formatter={formatCurrency}
                prefix={<StockOutlined className="text-orange-500" />}
                valueStyle={{ color: '#f59e0b' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Graphiques d'évolution */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} lg={12}>
            <Card 
              title="Évolution du chiffre d'affaires" 
              className="shadow-sm border-0 bg-white/60 backdrop-blur-sm h-96"
            >
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analytics.revenueEvolution}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColors.primary} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={chartColors.primary} stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    stroke="#6b7280"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    stroke="#6b7280"
                    tickFormatter={formatCurrency}
                  />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), 'Chiffre d\'affaires']}
                    labelFormatter={(date) => formatDateSafe(date, 'dd/MM/yyyy')}
                  />
                  <Area
                    type="monotone"
                    dataKey="total_revenue"
                    stroke={chartColors.primary}
                    fillOpacity={1}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card 
              title="Évolution des commandes" 
              className="shadow-sm border-0 bg-white/60 backdrop-blur-sm h-96"
            >
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analytics.ordersEvolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    stroke="#6b7280"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    stroke="#6b7280"
                  />
                  <Tooltip 
                    formatter={(value, name) => [formatNumber(value), 'Commandes']}
                    labelFormatter={(date) => formatDateSafe(date, 'dd/MM/yyyy')}
                  />
                  <Line
                    type="monotone"
                    dataKey="total_orders"
                    stroke={chartColors.secondary}
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Tableaux des tops */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} lg={12}>
            <Card 
              title="🏆 Top Produits" 
              className="shadow-sm border-0 bg-white/60 backdrop-blur-sm"
            >
              <Table
                dataSource={analytics.topProducts}
                columns={topProductsColumns}
                pagination={false}
                size="small"
                rowKey="product_id"
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card 
              title="⭐ Top Clients" 
              className="shadow-sm border-0 bg-white/60 backdrop-blur-sm"
            >
              <Table
                dataSource={analytics.topCustomers}
                columns={topCustomersColumns}
                pagination={false}
                size="small"
                rowKey="customer_id"
              />
            </Card>
          </Col>
          {/* <Col xs={24} lg={8}>
            <Card 
              title="💳 Ventes par mode de paiement" 
              className="shadow-sm border-0 bg-white/60 backdrop-blur-sm"
            >
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={analytics.salesByPaymentMethod}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="total_amount"
                  >
                    {analytics.salesByPaymentMethod.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={chartColors.gradient[index % chartColors.gradient.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [formatCurrency(value), 'Montant']} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col> */}
        </Row>

        {/* Commandes récentes */}
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card 
              title="🕒 Commandes récentes" 
              className="shadow-sm border-0 bg-white/60 backdrop-blur-sm"
            >
              <Table
                dataSource={analytics.recentOrders}
                columns={recentOrdersColumns}
                pagination={{ pageSize: 10 }}
                rowKey="id"
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default Analytics;
